/**
 * Comparables & Borrower Exit History DTOs
 *
 * Closed-system underwriting reference data: sale & rent comparables tied
 * to a Case, plus historical loan exits per borrower.
 *
 * @see Issue #1467 - Comparables + Exit History
 * @see Issue #1469 - BorrowerTier classifier (consumer of BorrowerExit)
 */

import { z } from 'zod';
import { BorrowerExitOutcome, CaseComparableKind } from '../enums/comparables.enums';
import { moneyString } from './common.dto';

// ============================================================================
// SHARED VALIDATION HELPERS
// ============================================================================

/**
 * Property address — denormalised JSON. Stored on each row so that
 * a comp / exit can stand on its own without joining back to a Case.
 */
export const ComparableAddressSchema = z.object({
  street: z.string().min(1).max(200),
  city: z.string().min(1).max(120),
  state: z.string().min(2).max(50),
  zip: z.string().min(3).max(20),
});
export type ComparableAddress = z.infer<typeof ComparableAddressSchema>;

const KindSchema = z.nativeEnum(CaseComparableKind);
const OutcomeSchema = z.nativeEnum(BorrowerExitOutcome);

const PriceDecimalString = z
  .union([z.string(), z.number()])
  .transform((v) => (typeof v === 'number' ? v.toString() : v))
  .pipe(moneyString('Invalid currency amount'));

const SmallDecimalString = z
  .union([z.string(), z.number()])
  .transform((v) => (typeof v === 'number' ? v.toString() : v))
  .pipe(moneyString('Invalid decimal amount'));

// ============================================================================
// CaseComparable DTO
// ============================================================================

export const CaseComparableRowSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  caseId: z.string().uuid(),
  kind: KindSchema,
  address: ComparableAddressSchema,
  beds: z.number().int().nonnegative().nullable(),
  baths: z.string().nullable(), // Decimal as string for precision
  sqft: z.number().int().nonnegative().nullable(),
  /** Money: sale price for SALE kind, monthly rent for RENT kind. */
  price: z.string().nullable(), // Decimal as string for precision
  /** SALE-specific: closing date. */
  closedAt: z.union([z.string(), z.date()]).nullable(),
  /** Distance from subject in miles. */
  distanceMi: z.string().nullable(),
  /** Source: MLS / Zillow / Redfin / Internal / etc. */
  source: z.string().max(64).nullable(),
  notes: z.string().max(2000).nullable(),
  enteredBy: z.string(),
  createdAt: z.union([z.string(), z.date()]).optional(),
  updatedAt: z.union([z.string(), z.date()]).optional(),
});
export type CaseComparableDto = z.infer<typeof CaseComparableRowSchema>;

export const CreateCaseComparableInputSchema = z.object({
  caseId: z.string().uuid(),
  kind: KindSchema,
  address: ComparableAddressSchema,
  beds: z.number().int().nonnegative().nullable().optional(),
  baths: SmallDecimalString.nullable().optional(),
  sqft: z.number().int().nonnegative().nullable().optional(),
  price: PriceDecimalString.nullable().optional(),
  closedAt: z.string().nullable().optional(),
  distanceMi: SmallDecimalString.nullable().optional(),
  source: z.string().max(64).nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
});
export type CreateCaseComparableInput = z.infer<typeof CreateCaseComparableInputSchema>;

export const UpdateCaseComparableInputSchema = CreateCaseComparableInputSchema.omit({
  caseId: true,
}).partial();
export type UpdateCaseComparableInput = z.infer<typeof UpdateCaseComparableInputSchema>;

// ============================================================================
// BorrowerExit DTO
// ============================================================================

export const BorrowerExitRowSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  consumerId: z.string().uuid(),
  outcome: OutcomeSchema,
  closedAt: z.union([z.string(), z.date()]),
  /** Optional caseId IF the exit was tracked in our system. */
  caseId: z.string().uuid().nullable(),
  propertyAddress: ComparableAddressSchema.nullable(),
  loanAmount: z.string().nullable(),
  purchasePrice: z.string().nullable(),
  salePrice: z.string().nullable(),
  daysToExit: z.number().int().nonnegative().nullable(),
  notes: z.string().max(2000).nullable(),
  enteredBy: z.string(),
  createdAt: z.union([z.string(), z.date()]).optional(),
  updatedAt: z.union([z.string(), z.date()]).optional(),
});
export type BorrowerExitDto = z.infer<typeof BorrowerExitRowSchema>;

export const CreateBorrowerExitInputSchema = z.object({
  consumerId: z.string().uuid(),
  outcome: OutcomeSchema,
  closedAt: z.string(),
  caseId: z.string().uuid().nullable().optional(),
  propertyAddress: ComparableAddressSchema.nullable().optional(),
  loanAmount: PriceDecimalString.nullable().optional(),
  purchasePrice: PriceDecimalString.nullable().optional(),
  salePrice: PriceDecimalString.nullable().optional(),
  daysToExit: z.number().int().nonnegative().nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
});
export type CreateBorrowerExitInput = z.infer<typeof CreateBorrowerExitInputSchema>;

export const UpdateBorrowerExitInputSchema = CreateBorrowerExitInputSchema.omit({
  consumerId: true,
}).partial();
export type UpdateBorrowerExitInput = z.infer<typeof UpdateBorrowerExitInputSchema>;
